import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Button } from "@bizform/primereact/button";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
BaseStateModel,
ComponentElement,
ComponentElementDisplayMode,
ComponentElementStatic,
ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import { CalendarSelectionMode, CalendarViewType } from "@bizform/type/model/data_set/LookUp.model";
import {
ComponentCategory,
ComponentElementManifest,
ComponentElementProperty,
ComponentElementPropertyType
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DataType } from "@bizform/type/model/model/DataType.model";
import {
    AccessibilityDate,
    Calendar,
    CalendarProps,
    ComponentHelper,
    DateHelper,
    DateMetaData,
    Debug,
    DomHelper,
    LocaleSettings
} from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { MultipleInputMaskDate } from "../../../misc/addon/inputmask/MultipleInputMaskDate";
import { AbstractComponentElement } from "../../AbstractComponentElement";
import { I18n } from "../../../misc/model/I18n.model";


let ___calendar;

type Props = CalendarProps & {};

type WrappedCompState = {
    id?: string;
    tabindex?: number;
    name?: string;
    value?: Array<Date>;
    viewDate?: Date;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    inline?: boolean;
    selectionMode?: string;
    inputId?: string;
    inputStyle?: object;
    inputClassName?: string;
    required?: boolean;
    readOnlyInput?: boolean;
    allowToType?: boolean;
    disabled?: boolean;
    placeholder?: string;
    showIcon?: boolean;
    icon?: string;
    showOnFocus?: boolean;
    numberOfMonths?: number;
    view?: string;
    touchUI?: boolean;
    showTime?: boolean;
    timeOnly?: boolean;
    showSeconds?: boolean;
    hourFormat?: string;
    stepHour?: number;
    stepMinute?: number;
    stepSecond?: number;
    shortYearCutoff?: string;
    hideOnDateTimeSelect?: boolean;
    locale?: LocaleSettings;
    dateFormat?: string;
    simpleDateFormat?: {input?: string, view?: string};
    panelStyle?: object;
    panelClassName?: string;
    monthNavigator?: boolean;
    yearNavigator?: boolean;
    disabledDates?: Date[];
    disabledDays?: number[];
    minDate?: Date;
    maxDate?: Date;
    maxDateCount?: number;
    showOtherMonths?: boolean;
    selectOtherMonths?: boolean;
    showButtonBar?: boolean;
    todayButtonClassName?: string;
    clearButtonStyleClass?: string;
    closeButtonStyleClass?: string;
    autoZIndex?: boolean;
    baseZIndex?: number;
    appendTo?: any;
    tooltipAttribute?: TooltipOptions & {text?: string};
    yearRange?: string;
    yearRangeFrom?: number;
    yearRangeTo?: number;

    dateTemplate?(dateMeta: DateMetaData): React.ReactNode;
    headerTemplate?(): React.ReactNode;
    footerTemplate?(): React.ReactNode;
    onFocus?(event: Event): void;
    onBlur?(event: Event): void;
    onInput?(event: Event): void;
    onSelect?(e: ComponentEventType): void;
    onChange?(e: ComponentEventType): void;
    onTodayButtonClick?(event: Event): void;
    onClearButtonClick?(event: Event): void;
    onCloseButtonClick?(event: Event): void;
    onViewDateChange?(e: ComponentEventType): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park
 * @class BfDatePickerMultiple
 * @description BfDatePickerMultiple component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Array<Date>>>()
export class BfDatePickerMultiple extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_HAZPu159zydc6Ge0VqH7nOsRdXz2JvZ",
        category: ComponentCategory.CALENDAR,
        label: I18n.calendar.multipleDatePicker.label,
        description: I18n.calendar.multipleDatePicker.description,
        icon: "",
        dataType: [DataType.DATE, DataType.UTCDATE],
        onlyArray: true,
        pathToLimitDataTypeArray: "maxDateCount",
        property: null
    };

    /* for React */
    props: Props;
    state: State;

    private _calendar: any;
    private _button: any;
    private _multipleInputMask: any;
    private _duplicated: any;
    private _tooltip: Array<any> = [];
    private _timer: NodeJS.Timer;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {},
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
        this._setRefBtn = this._setRefBtn.bind(this);
        this._setRefMultipleInputMask = this._setRefMultipleInputMask.bind(this);
        this.subscriptionResize = this.subscriptionResize.bind(this);
        this.closeOverlay = this.closeOverlay.bind(this);
        this.alignPanel = this.alignPanel.bind(this);
        this._onToggleOverlay = this._onToggleOverlay.bind(this);
        if (!___calendar) {
            ___calendar = {};
        }
    }

    @ReactComponentElementInit(BfDatePickerMultiple)
    static init(): ComponentElement<Array<Date>> {
        return;
    }

    focus(): void {
        if (this.accessibility) {
            this._calendar.validationFocus();
        } else if (this._calendar && this._calendar.showOverlay) {
            if (this._multipleInputMask && this._multipleInputMask.input) {
                this._multipleInputMask.focusInput();
            }
            this._calendar.showOverlay();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            this._removeCustom();
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            const newProps = newState.wrappedCompState;

            newProps.view = CalendarViewType.DATE;
            newProps.showIcon = true;

            if (newProps.minDate && "string" === typeof newProps.minDate) {
                newProps.minDate = new Date(newProps.minDate);
            }
            if (newProps.maxDate && "string" === typeof newProps.maxDate) {
                newProps.maxDate = new Date(newProps.maxDate);
            }

            newProps.selectOtherMonths = newProps.selectOtherMonths ? true : false;

            newProps.maxDateCount = !newProps.maxDateCount ? 1 : newProps.maxDateCount;
            let isAcceptable = true;
            if (newProps.value) {
                if (!Array.isArray(newProps.value)) {
                    newProps.value = [newProps.value];
                } else {
                    if (newProps.value.length > newProps.maxDateCount) {
                        newProps.value = newProps.value.slice(0, newProps.maxDateCount);
                        isAcceptable = false;
                    }
                }
            }
            const defaultDateFormatInput = DateHelper.getDefaultFormatByType(ComponentElementPropertyType.FORMATDATE);
            const defaultDateFormatView = DateHelper.getDefaultFormatByType(ComponentElementPropertyType.FORMATDATE);
            if (!newProps.simpleDateFormat.input || newProps.simpleDateFormat.input === "") {
                newProps.simpleDateFormat = {
                    input: !newProps.simpleDateFormat.input || newProps.simpleDateFormat.input === "" ? defaultDateFormatInput : newProps.simpleDateFormat.input,
                    view: !newProps.simpleDateFormat.view || newProps.simpleDateFormat.view === "" ? defaultDateFormatView : newProps.simpleDateFormat.view
                };
            }

            if (!newProps.viewDate) {
                newProps.viewDate = null;
            }

            if (isAcceptable) {
                this.setState({wrappedCompState: {...newProps}});
            } else {
                this.setState({wrappedCompState: {...newProps}}, this._callbackSetState);
            }
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: Array<Date>): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: Array<Date>): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: null
            }
        };
        if (!ComponentHelper.isNull(value)) {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    value.map((x) => {
                        if (!(x instanceof Date) || x.toString() !== "Invalid Date") {
                            return null;
                        }
                        return x;
                    });
                    newState.wrappedCompState.value = this.props.maxDateCount ? value.slice(this.props.maxDateCount, value.length) : value;
                }
            }
        }
        this.setState(newState);
    }

    subscriptionResize(): void {
        if (___calendar) {
            for (const panelName in ___calendar) {
                const panel = ___calendar[panelName];
                if (panel._timer) {
                    clearTimeout(panel._timer);
                }
                panel._timer = setTimeout(panel.alignPanel, 100);
            }
        }
    }

    initDone(): void {
        ___calendar[this.elementId] = this;
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    componentDidUpdate(): void {
        if (this.accessibility) {
            return;
        }
        if (this._calendar && this._calendar.container) {
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, this._button, this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
            if (this._calendar && this._calendar.inputElement) {
                this._calendar.inputElement.style.visibility = "hidden";
            }

            if (Array.isArray(this.state.wrappedCompState.value) && this.state.wrappedCompState.value.length > 0) {
                this._calendar.updateViewDate(null, this.state.wrappedCompState.value[this.state.wrappedCompState.value.length - 1]);
            } else {
                this._calendar.updateViewDate(null, new Date());
            }

            this.alignPanel();
        }

        if (this._multipleInputMask && this._multipleInputMask.input && !this._tooltip.length) {
            ComponentHelper.tooltip(this._multipleInputMask.input, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
        }
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._calendar = null;
        this._multipleInputMask = null;
        this._timer = null;
        delete ___calendar[this.elementId];
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitSubscriptionReject(): void {
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    closeOverlay() {
        if (this._calendar && this._calendar.hideOverlay) {
            this._calendar.hideOverlay();
        }
    }

    alignPanel() {
        if (this._calendar && this._calendar.alignPanel) {
            this._calendar.alignPanel();
        }
    }

    private _onChangeEvent(e: ComponentEventType): void {
        const newProps = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: e.value
            }
        };
        if (!this.accessibility && newProps.wrappedCompState.value && newProps.wrappedCompState.value.length === newProps.wrappedCompState.maxDateCount) {
            if (this._calendar) {
                this.closeOverlay();
            }
        }
        this.setState(newProps, this._callbackSetState);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            if (this.accessibility && displayMode !== ComponentElementDisplayMode.PRINT) {
                return this._render4Edit();
            }
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _multipleInputMaskOnChange(e): void {
        if (this._duplicated) {
            this._duplicated = undefined;
            return;
        } else {

            this.state.wrappedCompState.onChange({
                originalEvent: e,
                value: e.value
            });
        }
    }

    private _multipleInputMaskOnFocus(): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT && this.state.wrappedCompState.showOnFocus) {
            this._calendar.showOverlay();
        }
    }

    private _multipleInputMaskOnAdd(e): void {
        if (this.state.wrappedCompState.value) {
            this._duplicated = this.state.wrappedCompState.value.find( (val) => {
                return DateHelper.formatter(val, this.state.wrappedCompState.simpleDateFormat.view) === e.value;
            });
        }
    }

    private _onToggleOverlay() {
        if (this._calendar && this._multipleInputMask) {
            this._calendar.showOverlay();

        }
    }

    private _callbackSetState(): void {
        this.emit(this._getValueForEmit(this.state.wrappedCompState.value));
    }

    private _getValueForEmit(value: Date | Array<Date>): Array<Date> {
        return Array.isArray(value) ? value : [];
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        return (
            <React.Fragment>
                {this._renderInputMask(newProps)}
            </React.Fragment>
        );
    }

    private _renderInputMask(newProps: WrappedCompState) {
        if (this.accessibility) {
            return (
                <AccessibilityDate
                    tabindex={newProps.tabindex}
                    key={"Calendar_" + this.elementId}
                    ref={this._setRef}
                    id={this.elementId}
                    value={newProps.value}
                    displayMode={this.displayMode}
                    isArray={true}
                    locale={newProps.locale}
                    simpleDateFormat={newProps.simpleDateFormat}
                    maxDateCount={newProps.maxDateCount}
                    onChange={newProps.onChange}
                    tooltipAttribute={newProps.tooltipAttribute}
                />
            );
        } else {
            delete newProps.tooltipAttribute;
            let value = [];
            if (newProps.value && newProps.value.length) {
                for (let i = 0; i < newProps.value.length; i++) {
                    let invalid = true;
                    if (typeof newProps.value[i] === "string") {
                        if (newProps.value[i]) {
                            invalid = false;
                            value.push(newProps.value[i]);
                        }
                    } else {
                        const valid = newProps.value[i] && newProps.value[i].getDate && newProps.value[i].toString() !== "Invalid Date" ? true : false;
                        if (valid) {
                            invalid = false;
                            value.push(DateHelper.formatter(newProps.value[i], newProps.simpleDateFormat.view));
                        }
                    }
                    if (invalid) {
                        newProps.value.splice(i, 1);
                        i--;
                    }
                }
                newProps.placeholder = newProps.value.length === newProps.maxDateCount ? "" : newProps.placeholder;
                newProps.allowToType = newProps.value.length === newProps.maxDateCount ? false : newProps.allowToType;
            }
            return (
                <div style={{ position: "relative", width: "100%" }}>
                    <MultipleInputMaskDate
                        key={"MultipleInputMaskDate_" + this.elementId}
                        id={this.elementId + "_multipleInputMask"}
                        name={this.elementId + "_multipleInputMask"}
                        unmask={false}
                        autoClear={false}
                        style={newProps.showIcon === true ? {
                            width: "calc(100% - 30px)",
                            display: "inline-flex", ...newProps.inputStyle
                        } : { width: "100%", ...newProps.inputStyle }}
                        className={newProps.inputClassName}
                        placeholder={newProps.placeholder}
                        max={newProps.maxDateCount}
                        mask={newProps.simpleDateFormat.input}
                        viewMask={newProps.simpleDateFormat.view}
                        minDate={newProps.minDate}
                        maxDate={newProps.maxDate}
                        onChange={(e) => this._multipleInputMaskOnChange(e)}
                        value={value}
                        orgDateValue={newProps.value}
                        readOnly={this.state.displayMode === ComponentElementDisplayMode.READONLY}
                        readOnlyInput={!newProps.allowToType}
                        focusInput={() => this._multipleInputMaskOnFocus()}
                        disabled={newProps.disabled}
                        onAdd={(e) => this._multipleInputMaskOnAdd(e)}
                        onRemove={null}
                        ref={this._setRefMultipleInputMask}
                    />
                    <Button
                        disabled={(newProps as any).readOnly || newProps.disabled}
                        icon={this.state.wrappedCompState.icon} style={{
                            display: newProps.showIcon === true ? "inline" : "none",
                            position: "absolute",
                            top: 0
                        }}
                        ref={this._setRefBtn}
                        onClick={this._onToggleOverlay}
                    />
                    <Calendar
                        {...newProps}
                        key={"Calendar_" + this.elementId}
                        inputId={this.elementId}
                        selectionMode={CalendarSelectionMode.MULTIPLE}
                        readOnlyInput={!newProps.allowToType}
                        style={newProps.showIcon === true ? {
                            ...newProps.style,
                            width: "calc(100% - 30px)",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            zIndex: -1
                        } : { ...newProps.style, width: "100%", position: "absolute", top: 0, left: 0, zIndex: -1 }}
                        showIcon={false}
                        appendTo={DomHelper.getForOverLayDom()}
                        ref={this._setRef}
                    />
                </div>
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            allowToType: false,
            showOnFocus: false,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };
        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        let value: any = [];
        if (this.state.wrappedCompState.value) {
            value = this.state.wrappedCompState.value.map((x) => {
                let dateValue;
                if (typeof x === "string") {
                    dateValue = DateHelper.convertStringToDateByInputFormat(x as any, this.state.wrappedCompState.simpleDateFormat.input);
                    if (!dateValue || dateValue.toString() === "Invalid Date") {
                        return "";
                    }
                }
                return DateHelper.formatter(x, this.state.wrappedCompState.simpleDateFormat.view);
            }).join(", ");
        }
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{value}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._calendar = ref;
            this.readyComponent();
        }
    }

    private _setRefBtn(ref) {
        if (ref) {
            this._button = ref.element;
            this.readyComponent();
        }
    }

    private _setRefMultipleInputMask(ref) {
        if (ref) {
            if (this._tooltip.length) {
                ComponentHelper.unbindTooltip(this._tooltip);
            }
            this._multipleInputMask = ref;
        }
    }

    private _removeCustom(): void {
        if (this.accessibility) {
            return;
        }
        ComponentHelper.unbindTooltip(this._tooltip);
    }
}



